import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import FactCounter from '../components/FactCounter';

const Home: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const slides = [
    {
      image: "/img/photo1.png" 
    },
    {
      image: "/img/photo2.png"
    },
    {
      image: "/img/photo3.png"
    },
    {
      image: "/img/photo4.png"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.pageYOffset > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goals = [
    {
      icon: "fas fa-users",
      title: "Youth & Community Empowerment",
      description: "Empowering youth and communities through skill development, digital literacy, and access to opportunities that drive social and economic growth."
    },
    {
      icon: "fas fa-lightbulb",
      title: "Entrepreneurship Development",
      description: "Fostering entrepreneurial mindsets and supporting startups with training, resources, and mentorship to build sustainable businesses."
    },
    {
      icon: "fas fa-laptop-code",
      title: "Technology Adoption",
      description: "Promoting digital transformation and technology adoption across sectors to enhance productivity, efficiency, and innovation."
    },
    {
      icon: "fas fa-certificate",
      title: "Quality & Compliance",
      description: "Maintaining highest standards of quality and compliance in all our services, ensuring trust and excellence in every engagement."
    },
    {
      icon: "fas fa-chart-line",
      title: "Partner Growth",
      description: "Enabling our partners to grow and succeed through collaborative support, training, and access to resources and networks."
    },
    {
      icon: "fas fa-leaf",
      title: "Long-Term Sustainability",
      description: "Building sustainable models that create lasting impact for communities, partners, and the environment for generations to come."
    }
  ];

  return (
    <div className="home-page">
      {/* Carousel Hero Section */}
      <div className="relative w-full h-screen overflow-hidden bg-white">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img 
              src={slide.image}
              alt={`Slide ${index + 1}`}
              className="w-full h-full object-contain"
            />
          </div>
        ))}

        {/* Carousel Controls */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-primary hover:bg-secondary text-white w-12 h-12 rounded-full flex items-center justify-center transition z-20 shadow-lg"
        >
          <i className="fas fa-chevron-left"></i>
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-primary hover:bg-secondary text-white w-12 h-12 rounded-full flex items-center justify-center transition z-20 shadow-lg"
        >
          <i className="fas fa-chevron-right"></i>
        </button>

        {/* Carousel Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition ${
                index === currentSlide ? 'bg-secondary' : 'bg-gray-400'
              }`}
            ></button>
          ))}
        </div>
      </div>

      {/* About Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fadeInLeft">
              <div className="relative">
                <img 
                  src="/img/about-1.png" 
                  alt="About" 
                  className="rounded-lg shadow-lg w-full"
                />
                <div className="absolute -bottom-6 -right-6 bg-primary text-white p-8 rounded-lg shadow-xl">
                  <h2 className="text-4xl font-bold">10</h2>
                  <p className="text-sm">Years Experience</p>
                </div>
              </div>
            </div>

            <div className="animate-fadeInRight">
              <h5 className="text-primary font-bold text-lg mb-2 uppercase tracking-wide">About Us</h5>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                We Are Trusted For Your Business
              </h1>
              <p className="text-gray-600 mb-6">
                Nexedz Private Limited is a technology-driven organization empowering businesses, entrepreneurs, and institutions through digital, financial, and workforce solutions. We focus on sustainable growth by connecting innovation with real-world business needs.
              </p>
              <p className="text-gray-600 mb-8">
                Through our business units and authorized partners such as <span className="font-semibold text-primary">Edge Infotech</span> and <span className="font-semibold text-primary">Edge Paisa</span>, we deliver digital services, financial solutions, skill development, and manpower support across India—driving digital inclusion, employment, and entrepreneurship.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Fact Counter */}
      <FactCounter />

      {/* Services Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 animate-fadeInUp">
            <h1 className="text-4xl md:text-5xl font-bold">What We Offer</h1>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Service Card 1 */}
            <div className="bg-white rounded-lg shadow-lg p-8 hover:shadow-2xl transition duration-300 animate-fadeInUp group">
              <div className="bg-primary w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:bg-secondary transition">
                <i className="fas fa-code text-white text-2xl"></i>
              </div>
              <h4 className="text-xl font-bold mb-4 text-gray-800">Skill Development & Training</h4>
              <p className="text-gray-600">
                We deliver structured skill development programs designed to enhance employability and entrepreneurship among youth. Our training focuses on digital literacy, industry-relevant skills, and practical workforce readiness.
              </p>
            </div>

            {/* Service Card 2 */}
            <div className="bg-white rounded-lg shadow-lg p-8 hover:shadow-2xl transition duration-300 animate-fadeInUp group" style={{ animationDelay: '0.2s' }}>
              <div className="bg-primary w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:bg-secondary transition">
                <i className="fas fa-mobile-alt text-white text-2xl"></i>
              </div>
              <h4 className="text-xl font-bold mb-4 text-gray-800">Workforce & Employment Solutions</h4>
              <p className="text-gray-600">
                Nexedz connects skilled individuals with employment opportunities through manpower support, staffing solutions, and industry partnerships—helping organizations find the right talent while enabling livelihoods.
              </p>
            </div>

            {/* Service Card 3 */}
            <div className="bg-white rounded-lg shadow-lg p-8 hover:shadow-2xl transition duration-300 animate-fadeInUp group" style={{ animationDelay: '0.4s' }}>
              <div className="bg-primary w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:bg-secondary transition">
                <i className="fas fa-laptop-code text-white text-2xl"></i>
              </div>
              <h4 className="text-xl font-bold mb-4 text-gray-800">Financial Inclusion Services</h4>
              <p className="text-gray-600">
                Through authorized platforms and partnerships, we enable access to essential financial services such as savings accounts, digital payments, and assisted banking—promoting inclusive economic participation.
              </p>
            </div>

            {/* Service Card 4 */}
            <div className="bg-white rounded-lg shadow-lg p-8 hover:shadow-2xl transition duration-300 animate-fadeInUp group" style={{ animationDelay: '0.6s' }}>
              <div className="bg-primary w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:bg-secondary transition">
                <i className="fas fa-search text-white text-2xl"></i>
              </div>
              <h4 className="text-xl font-bold mb-4 text-gray-800">Institutional & Business Support</h4>
              <p className="text-gray-600">
                We work closely with institutions, NGOs, and organizations to implement programs related to skill development, digital inclusion, employment generation, and entrepreneurship support.
              </p>
            </div>

            {/* Service Card 5 */}
            <div className="bg-white rounded-lg shadow-lg p-8 hover:shadow-2xl transition duration-300 animate-fadeInUp group" style={{ animationDelay: '0.8s' }}>
              <div className="bg-primary w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:bg-secondary transition">
                <i className="fas fa-envelope-open-text text-white text-2xl"></i>
              </div>
              <h4 className="text-xl font-bold mb-4 text-gray-800">Partner & Centre Enablement</h4>
              <p className="text-gray-600">
                Nexedz empowers partners and centres with training, technology, and operational support to deliver services effectively across multiple regions.
              </p>
            </div>

            {/* Service Card 6 */}
            <div className="bg-white rounded-lg shadow-lg p-8 hover:shadow-2xl transition duration-300 animate-fadeInUp group" style={{ animationDelay: '1s' }}>
              <div className="bg-primary w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:bg-secondary transition">
                <i className="fas fa-headset text-white text-2xl"></i>
              </div>
              <h4 className="text-xl font-bold mb-4 text-gray-800">24/7 Support</h4>
              <p className="text-gray-600">
                Nexedz offers reliable 24/7 support to ensure seamless assistance whenever you need it. 
                Our dedicated team is always availableto support your business and customers.
              </p>  
            </div>
          </div>
        </div>
      </section>

      {/* Business Verticals Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 animate-fadeInUp">
            <h1 className="text-4xl md:text-5xl font-bold">Knowledge & Insights</h1>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Edge Infotech */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition duration-300 animate-fadeInUp flex flex-col">
              <div className="h-64 bg-primary overflow-hidden">
                <img 
                  src="/img/edge-infotech-logo.png"
                  alt="Edge Infotech"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 flex-grow flex flex-col">
                <h4 className="text-xl font-bold mb-3 text-gray-800">Edge Infotech</h4>
                <p className="text-gray-600 mb-6 flex-grow">
                  Edge Infotech is an authorized unit of Nexedz Private Limited, delivering technology-driven solutions in skill development, workforce support, and digital services. It focuses on empowering youth, institutions, and businesses through employment linkages.
                </p>
                <a 
                  href="https://edgeinfotech.co.in" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block bg-primary hover:bg-secondary text-white font-semibold py-3 px-8 rounded-full transition duration-300 text-center"
                >
                  Visit Now
                </a>
              </div>
            </div>

            {/* Edge Paisa */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition duration-300 animate-fadeInUp flex flex-col" style={{ animationDelay: '0.2s' }}>
              <div className="h-64 bg-primary overflow-hidden">
                <img 
                  src="/img/edge-paisa-logo.png"
                  alt="Edge Paisa"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 flex-grow flex flex-col">
                <h4 className="text-xl font-bold mb-3 text-gray-800">Edge Paisa</h4>
                <p className="text-gray-600 mb-6 flex-grow">
                  Edge Paisa is a digital financial services platform that enables easy access to savings accounts, digital payments, and assisted banking solutions. It promotes financial inclusion by connecting individuals and communities to secure and reliable financial services.
                </p>
                <a 
                  href="https://www.edgepaisa.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block bg-primary hover:bg-secondary text-white font-semibold py-3 px-8 rounded-full transition duration-300 text-center"
                >
                  Visit Now
                </a>
              </div>
            </div>

            {/* Rojgar Mitra */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition duration-300 animate-fadeInUp flex flex-col" style={{ animationDelay: '0.4s' }}>
              <div className="h-64 bg-primary overflow-hidden">
                <img 
                  src="/img/rojgar-mitra-logo.png"
                  alt="Rojgar Mitra"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 flex-grow flex flex-col">
                <h4 className="text-xl font-bold mb-3 text-gray-800">Rojgar Mitra</h4>
                <p className="text-gray-600 mb-6 flex-grow">
                  Rojgar Mitra is an employment support initiative that connects skilled individuals with suitable job opportunities. It works as a bridge between employers and job seekers to promote sustainable livelihoods and workforce growth.
                </p>
                <a 
                  href="https://nexedz.org/rojgar-mitra" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block bg-primary hover:bg-secondary text-white font-semibold py-3 px-8 rounded-full transition duration-300 text-center"
                >
                  Visit Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Nexedz Goals Section */}
      <section className="py-20 bg-gradient-to-br from-primary to-primary-dark">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 animate-fadeInUp">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Nexedz Goals</h1>
            <p className="text-gray-200 max-w-3xl mx-auto">
              Committed to creating sustainable impact through innovation, empowerment, and excellence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {goals.map((goal, index) => (
              <div 
                key={index}
                className="bg-white rounded-lg shadow-xl p-8 hover:shadow-2xl hover:transform hover:-translate-y-2 transition duration-300 animate-fadeInUp group"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="bg-secondary w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:bg-primary transition duration-300">
                  <i className={`${goal.icon} text-white text-2xl`}></i>
                </div>
                <h4 className="text-xl font-bold mb-4 text-gray-800 group-hover:text-primary transition duration-300">
                  {goal.title}
                </h4>
                <p className="text-gray-600 leading-relaxed">
                  {goal.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-blue-500 hover:bg-blue-500 text-white w-12 h-12 rounded-full flex items-center justify-center transition duration-300 shadow-lg z-50 hover:scale-110"
          aria-label="Scroll to top"
        >
          <i className="fas fa-arrow-up"></i>
        </button>
      )}
    </div>
  );
};

export default Home;